import { Form, Select } from "antd";
import { AppPartType } from "@lhd/features/Apps/AppForm/types";
import { Styles } from "./styled";
import { IOptions } from "@lhd/types";
import {
  getConfigAppPartType,
  renderSelection,
} from "@lhd/features/Apps/utils";
import { getRuleValidation } from "@lhd/components/CloneApp/fieldUtils";
import { APP_TYPES } from "../../constants";
import { useEffect, useMemo } from "react";
import { FormInstance } from "antd/es/form/Form";
import { useFetchDynamicOrgsForBusinessTrip } from "./hooks/useFetchDynamicOrgsForBusinessTrip";

interface IProps {
  item: AppPartType;
  parentItem?: AppPartType;
  style?: React.CSSProperties;
  isTable?: boolean;
  namePath: string | string[];
  styleColor?: React.CSSProperties;
  options: IOptions;
  loading?: boolean;
  isDisabled?: boolean;
  appType?: string;
  appParts?: AppPartType[];
  form?: FormInstance;
}

const CommonSelection = (props: IProps) => {
  const {
    item,
    namePath,
    style,
    styleColor,
    options,
    loading,
    isDisabled,
    appType,
    appParts,
    form: formProps,
  } = props;
  const { appPartType } = getConfigAppPartType(item);
  const isSingleSelect =
    appType === APP_TYPES.BUSINESS_TRIP_REPORTS &&
    (item.field_code === "business_trip_expense_applicant" ||
      item.field_code === "department_selection");

  const form = Form.useFormInstance() || formProps;

  const { isDepartmentField, workerId, dynamicOptions, isOrgsLoading } =
    useFetchDynamicOrgsForBusinessTrip({
      appType,
      item,
      appParts,
      namePath,
      form,
    });

  const selectOptions = useMemo(() => {
    if (isDepartmentField) {
      return workerId ? dynamicOptions : [];
    }
    return options;
  }, [isDepartmentField, workerId, dynamicOptions, options]);

  const selectLoading =
    loading || (isDepartmentField && !!workerId && isOrgsLoading);

  const currentValue = Form.useWatch(namePath, form);

  useEffect(() => {
    if (!form || selectLoading) return;
    if (isDepartmentField && !workerId) return;
    if (currentValue == null || currentValue === "") return;
    if (typeof currentValue === "string" && currentValue.startsWith("[")) {
      return;
    }

    const validValues = new Set(selectOptions.map((o) => String(o.value)));
    const isValid = (value: unknown) => validValues.has(String(value));

    if (Array.isArray(currentValue)) {
      const nextValue = currentValue.filter(isValid);
      if (nextValue.length !== currentValue.length) {
        form.setFieldValue(namePath, nextValue);
      }
    } else if (!isValid(currentValue)) {
      form.setFieldValue(namePath, undefined);
    }
  }, [
    form,
    namePath,
    currentValue,
    selectOptions,
    selectLoading,
    isDepartmentField,
    workerId,
  ]);

  return (
    <Styles
      style={style}
      color={styleColor?.color}
      background={styleColor?.background}
    >
      <Form.Item name={namePath} rules={getRuleValidation(item)}>
        <Select
          showSearch
          loading={selectLoading}
          mode={isSingleSelect ? undefined : "multiple"}
          tagRender={(props: any) =>
            selectOptions?.length ? renderSelection(props, appPartType) : <></>
          }
          disabled={isDisabled}
          filterOption={(input, option: any) =>
            (option?.children ?? "")
              .toLowerCase()
              .includes(input.trim().toLowerCase())
          }
        >
          {selectOptions.map((option, i) => {
            return (
              <Select.Option
                key={i}
                value={option.value}
                className="custom-option"
              >
                {option.label}
              </Select.Option>
            );
          })}
        </Select>
      </Form.Item>
    </Styles>
  );
};
export default CommonSelection;
